import { Fragment, useState } from "react";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
  Dialog,
} from "@headlessui/react";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonThird from "shared/Button/ButtonThird";
import Checkbox from "shared/Checkbox/Checkbox";
import convertNumbThousand from "utils/convertNumbThousand";
import Slider from "rc-slider";
import { FunnelIcon } from "@heroicons/react/24/outline";

const TabFilters = ({
  types,
  makes,
  models,
  selectedTypes,
  selectedMakes,
  selectedModels,
  setSelectedTypes,
  setSelectedMakes,
  setSelectedModels,
  rangePrices,
  setRangePrices,
  maxPrice,
  sortOrder,
  setSortOrder,
  showMakeFilter = true,
  showModelFilter = true,
}: {
  types: string[];
  makes: string[];
  models: string[];
  selectedTypes: string[];
  selectedMakes: string[];
  selectedModels: string[];
  setSelectedTypes: (types: string[]) => void;
  setSelectedMakes: (makes: string[]) => void;
  setSelectedModels: (models: string[]) => void;
  rangePrices: number[];
  setRangePrices: (prices: number[]) => void;
  maxPrice: number;
  sortOrder: string;
  setSortOrder: (order: string) => void;
  showMakeFilter?: boolean;
  showModelFilter?: boolean;
}) => {
  const [selectedAllTypes, setSelectedAllTypes] = useState<boolean>(false);
  const [selectedAllMakes, setSelectedAllMakes] = useState<boolean>(false);
  const [selectedAllModels, setSelectedAllModels] = useState<boolean>(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Common button class for consistent width
  const filterButtonClass =
    "flex items-center justify-between px-4 py-2 text-sm rounded-md border border-secondary-100 hover:border-secondary-100 focus:outline-none bg-bg-color text-text-color w-32 min-w-[128px]";

  // Common panel content class with fixed height and scrolling
  const panelContentClass =
    "relative flex flex-col px-5 py-6 space-y-5 max-h-72 overflow-y-auto";

  const customSort = (a: string, b: string) => {
    const isANumeric = /^\d/.test(a);
    const isBNumeric = /^\d/.test(b);
    const isASpecial = /^[^a-zA-Z0-9]/.test(a);
    const isBSpecial = /^[^a-zA-Z0-9]/.test(b);

    // Special characters come last
    if (isASpecial && !isBSpecial) return 1;
    if (!isASpecial && isBSpecial) return -1;
    if (isASpecial && isBSpecial) return a.localeCompare(b);

    // Numbers after letters
    if (isANumeric && !isBNumeric) return 1;
    if (!isANumeric && isBNumeric) return -1;

    // Regular alphabetical/numerical comparison
    return a.localeCompare(b, undefined, { numeric: true });
  };

  const renderTabsTypeOfCars = () => {
    return (
      <Popover className="relative">
        {({ open, close }) => (
          <>
            <PopoverButton
              className={`${filterButtonClass} ${
                open ? "!border-primary-500 " : ""
              }`}
            >
              <span className="text-text-color">Category</span>
              <i className="las la-angle-down"></i>
            </PopoverButton>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <PopoverPanel className="absolute z-10 w-screen max-w-sm px-4 mt-3 right-0 lg:left-auto lg:right-0 md:left-0 sm:px-0">
                <div className="overflow-hidden rounded-md shadow-xl bg-bg-color border border-secondary-100">
                  <div className={panelContentClass}>
                    <div key={"Select All"} className="">
                      <Checkbox
                        name="Select All"
                        label="Select All"
                        defaultChecked={selectedAllTypes}
                        onChange={(checked) => {
                          setSelectedAllTypes(checked);
                          setSelectedTypes(checked ? types : []); // Select all types or clear all types
                        }}
                        truncate={true}
                      />
                    </div>

                    {types.map((type) => (
                      <div key={type} className="">
                        <Checkbox
                          name={type}
                          label={type}
                          title={type}
                          defaultChecked={selectedTypes.includes(type)}
                          onChange={(checked) => {
                            if (checked) {
                              setSelectedTypes([...selectedTypes, type]);
                              if (selectedTypes.length + 1 === types.length) {
                                setSelectedAllTypes(true); // All types are selected
                              }
                            } else {
                              setSelectedTypes(
                                selectedTypes.filter((t) => t !== type)
                              );
                              setSelectedAllTypes(false); // Deselect 'Select All' if any type is unchecked
                            }
                          }}
                          truncate={true}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="p-5 bg-bg-color flex items-center justify-between">
                    <ButtonThird
                      onClick={() => {
                        setSelectedAllTypes(false);
                        setSelectedTypes([]);
                      }}
                      sizeClass="px-4 py-2 sm:px-5"
                    >
                      Clear
                    </ButtonThird>
                    <ButtonPrimary
                      onClick={close}
                      sizeClass="px-4 py-2 sm:px-5"
                    >
                      Apply
                    </ButtonPrimary>
                  </div>
                </div>
              </PopoverPanel>
            </Transition>
          </>
        )}
      </Popover>
    );
  };

  const renderTabsMakes = () => {
    const sortedMakes = [...makes].sort(customSort);
    
    return (
      <Popover className="relative">
        {({ open, close }) => (
          <>
            <PopoverButton
              className={`${filterButtonClass} ${
                open ? "!border-primary-500 " : ""
              }`}
            >
              <span className="text-text-color">Make</span>
              <i className="las la-angle-down"></i>
            </PopoverButton>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <PopoverPanel className="absolute z-10 w-screen max-w-sm px-4 mt-3 right-0 lg:left-auto lg:right-0 md:right-0 md:left-auto sm:px-0">
                <div className="overflow-hidden rounded-md shadow-xl bg-bg-color border border-secondary-100">
                  <div className={panelContentClass}>
                    <div key={"Select All"} className="">
                      <Checkbox
                        name="Select All Makes"
                        label="Select All"
                        defaultChecked={selectedAllMakes}
                        onChange={(checked) => {
                          setSelectedAllMakes(checked);
                          setSelectedMakes(checked ? makes : []);
                        }}
                        truncate={true}
                      />
                    </div>

                    {sortedMakes.map((make) => (
                      <div key={make} className="">
                        <Checkbox
                          name={make}
                          label={make}
                          title={make}
                          defaultChecked={selectedMakes.includes(make)}
                          onChange={(checked) => {
                            if (checked) {
                              setSelectedMakes([...selectedMakes, make]);
                              if (selectedMakes.length + 1 === makes.length) {
                                setSelectedAllMakes(true);
                              }
                            } else {
                              setSelectedMakes(
                                selectedMakes.filter((m) => m !== make)
                              );
                              setSelectedAllMakes(false);
                            }
                          }}
                          truncate={true}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="p-5 bg-bg-color flex items-center justify-between">
                    <ButtonThird
                      onClick={() => {
                        setSelectedAllMakes(false);
                        setSelectedMakes([]);
                      }}
                      sizeClass="px-4 py-2 sm:px-5"
                    >
                      Clear
                    </ButtonThird>
                    <ButtonPrimary
                      onClick={close}
                      sizeClass="px-4 py-2 sm:px-5"
                    >
                      Apply
                    </ButtonPrimary>
                  </div>
                </div>
              </PopoverPanel>
            </Transition>
          </>
        )}
      </Popover>
    );
  };

  const renderTabsModels = () => {
    const sortedModels = [...models].sort(customSort);
    
    return (
      <Popover className="relative">
        {({ open, close }) => (
          <>
            <PopoverButton
              className={`${filterButtonClass} ${
                open ? "!border-primary-500 " : ""
              }`}
            >
              <span className="text-text-color">Model</span>
              <i className="las la-angle-down"></i>
            </PopoverButton>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <PopoverPanel className="absolute z-10 w-screen max-w-sm px-4 mt-3 right-0 lg:left-auto lg:right-0 md:right-0 md:left-auto sm:px-0">
                <div className="overflow-hidden rounded-md shadow-xl bg-bg-color border border-secondary-100">
                  <div className={panelContentClass}>
                    <div key={"Select All"} className="">
                      <Checkbox
                        name="Select All Models"
                        label="Select All"
                        defaultChecked={selectedAllModels}
                        onChange={(checked) => {
                          setSelectedAllModels(checked);
                          setSelectedModels(checked ? models : []);
                        }}
                        truncate={true}
                      />
                    </div>

                    {sortedModels.map((model) => (
                      <div key={model} className="">
                        <Checkbox
                          name={model}
                          label={model}
                          title={model}
                          defaultChecked={selectedModels.includes(model)}
                          onChange={(checked) => {
                            if (checked) {
                              setSelectedModels([...selectedModels, model]);
                              if (selectedModels.length + 1 === models.length) {
                                setSelectedAllModels(true);
                              }
                            } else {
                              setSelectedModels(
                                selectedModels.filter((m) => m !== model)
                              );
                              setSelectedAllModels(false);
                            }
                          }}
                          truncate={true}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="p-5 bg-bg-color flex items-center justify-between">
                    <ButtonThird
                      onClick={() => {
                        setSelectedAllModels(false);
                        setSelectedModels([]);
                      }}
                      sizeClass="px-4 py-2 sm:px-5"
                    >
                      Clear
                    </ButtonThird>
                    <ButtonPrimary
                      onClick={close}
                      sizeClass="px-4 py-2 sm:px-5"
                    >
                      Apply
                    </ButtonPrimary>
                  </div>
                </div>
              </PopoverPanel>
            </Transition>
          </>
        )}
      </Popover>
    );
  };

  const renderTabsPriceRage = () => {
    const handlePriceInput = (value: string, isMin: boolean) => {
      const numericValue = value.replace(/[^0-9]/g, '');
      const numValue = numericValue === '' ? 0 : parseInt(numericValue, 10);
      
      if (isMin) {
        setRangePrices([numValue, rangePrices[1]]);
      } else {
        setRangePrices([rangePrices[0], numValue]);
      }
    };

    return (
      <Popover className="relative">
        {({ open, close }) => (
          <>
            <PopoverButton
              className={`${filterButtonClass} ${
                open ? "!border-primary-500 " : ""
              }`}
            >
              <span className="truncate text-text-color">
                {`$${convertNumbThousand(
                  rangePrices[0]
                )} - $${convertNumbThousand(rangePrices[1])}`}
              </span>
              <i className="las la-angle-down"></i>
            </PopoverButton>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <PopoverPanel className="absolute z-10 w-screen max-w-sm px-4 mt-3 right-0 lg:left-auto lg:right-0 md:right-0 md:left-auto sm:px-0">
                <div className="overflow-hidden rounded-md shadow-xl bg-bg-color border border-secondary-100">
                  <div className="relative flex flex-col px-5 py-6 space-y-8">
                    <div className="space-y-5">
                      <span className="font-medium">Price per day</span>
                      <Slider
                        range
                        className="text-red-400"
                        min={0}
                        max={maxPrice}
                        value={rangePrices}
                        allowCross={false}
                        onChange={(e) => setRangePrices(e as number[])}
                      />
                    </div>

                    <div className="flex justify-between space-x-5">
                      <div>
                        <label
                          htmlFor="minPrice"
                          className="block text-sm font-medium text-text-color "
                        >
                          Min price
                        </label>
                        <div className="mt-1 relative rounded-md">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-text-color sm:text-sm">$</span>
                          </div>
                          <input
                            type="text"
                            name="minPrice"
                            id="minPrice"
                            className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-3 sm:text-sm border-secondary-100 rounded-md text-text-color"
                            value={rangePrices[0]}
                            onChange={(e) => handlePriceInput(e.target.value, true)}
                            onKeyDown={(e) => {
                              // Allow only numbers, backspace, delete, arrow keys, tab
                              if (
                                !/^\d$/.test(e.key) && 
                                !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)
                              ) {
                                e.preventDefault();
                              }
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label
                          htmlFor="maxPrice"
                          className="block text-sm font-medium text-text-color "
                        >
                          Max price
                        </label>
                        <div className="mt-1 relative rounded-md">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-text-color sm:text-sm">$</span>
                          </div>
                          <input
                            type="text"
                            name="maxPrice"
                            id="maxPrice"
                            className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-3 sm:text-sm border-secondary-100 rounded-md text-text-color"
                            value={rangePrices[1]}
                            onChange={(e) => handlePriceInput(e.target.value, false)}
                            onKeyDown={(e) => {
                              // Allow only numbers, backspace, delete, arrow keys, tab
                              if (
                                !/^\d$/.test(e.key) && 
                                !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)
                              ) {
                                e.preventDefault();
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-5 bg-bg-color flex items-center justify-between">
                    <ButtonThird
                      onClick={() => setRangePrices([0, maxPrice])}
                      sizeClass="px-4 py-2 sm:px-5"
                    >
                      Clear
                    </ButtonThird>
                    <ButtonPrimary
                      onClick={close}
                      sizeClass="px-4 py-2 sm:px-5"
                    >
                      Apply
                    </ButtonPrimary>
                  </div>
                </div>
              </PopoverPanel>
            </Transition>
          </>
        )}
      </Popover>
    );
  };

  const renderSortOptions = () => {
    return (
      <Popover className="relative">
        {({ open, close }) => (
          <>
            <PopoverButton
              className={`${filterButtonClass} ${
                open ? "!border-primary-500 " : ""
              }`}
            >
              <span className="text-text-color">Sort</span>
              <i className="las la-angle-down"></i>
            </PopoverButton>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <PopoverPanel className="absolute z-10 w-screen max-w-sm px-4 mt-3 right-0 lg:left-auto lg:right-1 md:left-0 sm:px-0">
                <div className="overflow-hidden rounded-md shadow-xl bg-bg-color border border-secondary-100">
                  <div className="relative flex flex-col px-5 py-6 space-y-5">
                    {[
                      { id: "default", name: "Default" },
                      { id: "aToZ", name: "A to Z" },
                      { id: "zToA", name: "Z to A" },
                      { id: "priceLowToHigh", name: "Price: Low to High" },
                      { id: "priceHighToLow", name: "Price: High to Low" },
                    ].map((option) => (
                      <div key={option.id} className="">
                        <Checkbox
                          name={option.id}
                          label={option.name}
                          title={option.name}
                          defaultChecked={sortOrder === option.id}
                          onChange={() => setSortOrder(option.id)}
                          truncate={true}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="p-5 bg-bg-color flex items-center justify-between">
                    <ButtonThird
                      onClick={() => {
                        setSortOrder("default");
                      }}
                      sizeClass="px-4 py-2 sm:px-5"
                    >
                      Reset
                    </ButtonThird>
                    <ButtonPrimary
                      onClick={close}
                      sizeClass="px-4 py-2 sm:px-5"
                    >
                      Apply
                    </ButtonPrimary>
                  </div>
                </div>
              </PopoverPanel>
            </Transition>
          </>
        )}
      </Popover>
    );
  };

  // Mobile panel: headings + checkboxes in grid layout
  const handleClearFilters = () => {
    setSelectedAllTypes(false);
    setSelectedTypes([]);
    setSelectedAllMakes(false);
    setSelectedMakes([]);
    setSelectedAllModels(false);
    setSelectedModels([]);
    setRangePrices([0, maxPrice]);
    setSortOrder("default");
  };

  const renderMobilePanel = () => (
    <Dialog
      open={mobileOpen}
      onClose={() => setMobileOpen(false)}
      className="fixed inset-0 z-50 flex items-center justify-center"
    >
      <div className="fixed inset-0 bg-black bg-opacity-40" />
      <div className="relative bg-bg-color w-full mx-auto h-full overflow-y-auto shadow-xl rounded-lg flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-100">
          <span className="text-lg font-semibold text-text-color">Filters</span>
          <button
            className="p-2 rounded-full hover:bg-neutral-100"
            onClick={() => setMobileOpen(false)}
            aria-label="Close"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>
        <div className="flex flex-col gap-6 px-6 py-4 overflow-y-auto">
          {/* Category */}
          <div>
            <div className="font-semibold mb-2 text-text-color">Category</div>
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
              <Checkbox
                name="Select All"
                label="Select All"
                defaultChecked={selectedAllTypes}
                onChange={(checked) => {
                  setSelectedAllTypes(checked);
                  setSelectedTypes(checked ? types : []);
                }}
                truncate={true}
              />
              {types.map((type) => (
                <Checkbox
                  key={type}
                  name={type}
                  label={type}
                  title={type}
                  defaultChecked={selectedTypes.includes(type)}
                  onChange={(checked) => {
                    if (checked) {
                      setSelectedTypes([...selectedTypes, type]);
                      if (selectedTypes.length + 1 === types.length) {
                        setSelectedAllTypes(true);
                      }
                    } else {
                      setSelectedTypes(selectedTypes.filter((t) => t !== type));
                      setSelectedAllTypes(false);
                    }
                  }}
                  truncate={true}
                />
              ))}
            </div>
          </div>
          
          {/* Make */}
          {showMakeFilter && makes.length > 0 && (
            <div>
              <div className="font-semibold mb-2 text-text-color">Make</div>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                <Checkbox
                  name="Select All Makes"
                  label="Select All"
                  defaultChecked={selectedAllMakes}
                  onChange={(checked) => {
                    setSelectedAllMakes(checked);
                    setSelectedMakes(checked ? makes : []);
                  }}
                  truncate={true}
                />
                {[...makes].sort(customSort).map((make) => (
                  <Checkbox
                    key={make}
                    name={make}
                    label={make}
                    title={make}
                    defaultChecked={selectedMakes.includes(make)}
                    onChange={(checked) => {
                      if (checked) {
                        setSelectedMakes([...selectedMakes, make]);
                        if (selectedMakes.length + 1 === makes.length) {
                          setSelectedAllMakes(true);
                        }
                      } else {
                        setSelectedMakes(selectedMakes.filter((m) => m !== make));
                        setSelectedAllMakes(false);
                      }
                    }}
                    truncate={true}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Model */}
          {showModelFilter && models.length > 0 && (
            <div>
              <div className="font-semibold mb-2 text-text-color">Model</div>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                <Checkbox
                  name="Select All Models"
                  label="Select All"
                  defaultChecked={selectedAllModels}
                  onChange={(checked) => {
                    setSelectedAllModels(checked);
                    setSelectedModels(checked ? models : []);
                  }}
                  truncate={true}
                />
                {[...models].sort(customSort).map((model) => (
                  <Checkbox
                    key={model}
                    name={model}
                    label={model}
                    title={model}
                    defaultChecked={selectedModels.includes(model)}
                    onChange={(checked) => {
                      if (checked) {
                        setSelectedModels([...selectedModels, model]);
                        if (selectedModels.length + 1 === models.length) {
                          setSelectedAllModels(true);
                        }
                      } else {
                        setSelectedModels(selectedModels.filter((m) => m !== model));
                        setSelectedAllModels(false);
                      }
                    }}
                    truncate={true}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Price Range */}
          <div>
            <div className="font-semibold mb-2 text-text-color">Price per day</div>
            <div className="space-y-4">
              <Slider
                range
                className="text-red-400"
                min={0}
                max={maxPrice}
                value={rangePrices}
                allowCross={false}
                onChange={(e) => setRangePrices(e as number[])}
              />
              <div className="flex justify-between space-x-5">
                <div>
                  <label
                    htmlFor="minPriceMobile"
                    className="block text-sm font-medium text-text-color "
                  >
                    Min price
                  </label>
                  <div className="mt-1 relative rounded-md">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-text-color sm:text-sm">$</span>
                    </div>
                    <input
                      type="text"
                      name="minPriceMobile"
                      id="minPriceMobile"
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-3 sm:text-sm border-secondary-100 rounded-md text-text-color"
                      value={rangePrices[0]}
                      onChange={(e) => {
                        const numericValue = e.target.value.replace(/[^0-9]/g, '');
                        const numValue = numericValue === '' ? 0 : parseInt(numericValue, 10);
                        setRangePrices([numValue, rangePrices[1]]);
                      }}
                      onKeyDown={(e) => {
                        if (!/^\d$/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="maxPriceMobile"
                    className="block text-sm font-medium text-text-color "
                  >
                    Max price
                  </label>
                  <div className="mt-1 relative rounded-md">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-text-color sm:text-sm">$</span>
                    </div>
                    <input
                      type="text"
                      name="maxPriceMobile"
                      id="maxPriceMobile"
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-3 sm:text-sm border-secondary-100 rounded-md text-text-color"
                      value={rangePrices[1]}
                      onChange={(e) => {
                        const numericValue = e.target.value.replace(/[^0-9]/g, '');
                        const numValue = numericValue === '' ? 0 : parseInt(numericValue, 10);
                        setRangePrices([rangePrices[0], numValue]);
                      }}
                      onKeyDown={(e) => {
                        if (!/^\d$/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Sort */}
          <div>
            <div className="font-semibold mb-2 text-text-color">Sort</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: "default", name: "Default" },
                { id: "aToZ", name: "A to Z" },
                { id: "zToA", name: "Z to A" },
                { id: "priceLowToHigh", name: "Price: Low to High" },
                { id: "priceHighToLow", name: "Price: High to Low" },
              ].map((option) => (
                <Checkbox
                  key={option.id}
                  name={option.id}
                  label={option.name}
                  title={option.name}
                  defaultChecked={sortOrder === option.id}
                  onChange={() => setSortOrder(option.id)}
                  truncate={true}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-secondary-100 flex justify-between gap-2 mt-auto">
          <ButtonThird onClick={handleClearFilters}>
            Clear
          </ButtonThird>
          <ButtonPrimary onClick={() => setMobileOpen(false)}>
            Apply Filters
          </ButtonPrimary>
        </div>
      </div>
    </Dialog>
  );

  // Determine if any filter is applied
  const isFilterApplied =
    selectedTypes.length > 0 ||
    selectedMakes.length > 0 ||
    selectedModels.length > 0 ||
    (rangePrices[0] !== 0 || rangePrices[1] !== maxPrice) ||
    sortOrder !== "default";

  return (
    <div className=" mt-4 flex lg:mt-0 lg:gap-4 lg:justify-end">
      {/* Desktop filters */}
      <div className="hidden lg:flex flex-wrap gap-2 justify-end md:justify-start">
        {renderTabsTypeOfCars()}
        {showMakeFilter && makes.length > 0 && renderTabsMakes()}
        {showModelFilter && models.length > 0 && renderTabsModels()}
        {renderTabsPriceRage()}
        {renderSortOptions()}
      </div>
      {/* Mobile filter icon */}
      <div className="flex lg:hidden w-full justify-end">
        <button
          className={`p-2 rounded-full border border-secondary-100 bg-bg-color flex items-center ${isFilterApplied ? "bg-primary-700 text-white" : "text-text-color"}`}
          onClick={() => setMobileOpen(true)}
          aria-label="Open Filters"
        >
          <FunnelIcon className="h-6 w-6 text-text-color" />
        </button>
        {mobileOpen && renderMobilePanel()}
      </div>
    </div>
  );
};

export default TabFilters;
